import React from "react";
import { Box, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDownloadDigitalCOCMutation } from "../api/apiSlice";
import { BG, CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { useDispatch } from "react-redux";
import { setlogo } from "./Nav/navSlice";

const primaryButtonSx = {
  backgroundColor: AMBER,
  color: "#0d1117",
  fontWeight: 700,
  borderRadius: "8px",
  px: "20px",
  py: "10px",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#e5a730", boxShadow: "none" },
};

const secondaryButtonSx = {
  color: "rgba(255,255,255,0.75)",
  border: `1px solid ${BORDER}`,
  borderRadius: "8px",
  px: "20px",
  py: "10px",
  textTransform: "none",
  fontWeight: 600,
};

function OutcomeLayout({ icon, title, children, actions }) {
  return (
    <>
      <Helmet><style>{`body { background: ${BG}; }`}</style></Helmet>
      <Box sx={{ px: "24px", pt: "100px", pb: "48px", maxWidth: "480px", mx: "auto", textAlign: "center" }}>
        <Box sx={{ mb: "16px" }}>{icon}</Box>
        <Typography sx={{ fontSize: "1.4rem", fontWeight: 800, color: "#fff", mb: "10px" }}>{title}</Typography>
        <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", p: "16px 18px", mb: "24px", textAlign: "left" }}>
          {children}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>{actions}</Box>
      </Box>
    </>
  );
}

// A job opened from a stale page: someone has already approved or declined it. The
// details page used to render nothing (a black screen), and the COC page left the QE on
// the capture form with only a back arrow, which led to that black screen (N7).
export function NoLongerPending({ history = false }) {
  const navigate = useNavigate();
  return (
    <OutcomeLayout
      icon={<InfoOutlinedIcon sx={{ fontSize: "2.6rem", color: AMBER }} />}
      title={history ? "Job not found" : "No longer awaiting review"}
      actions={
        <>
          {/* replace: Back from here returned to the actioned job's capture form (P5). */}
          <Button variant="contained" onClick={() => navigate(history ? "/QE/history/" : "/QE/pending-installs/", { replace: true })} sx={primaryButtonSx}>
            {history ? "Back to history" : "Back to pending installs"}
          </Button>
          <Button onClick={() => navigate("/QE/", { replace: true })} sx={secondaryButtonSx}>QE home</Button>
        </>
      }
    >
      <Typography sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.8)", lineHeight: 1.6 }}>
        {history
          ? "This job isn't in your recent QE history."
          : "This job has already been approved or declined, so there's nothing left to review here."}
      </Typography>
    </OutcomeLayout>
  );
}

// After an approval: a screen rather than a closable dialog (D7), so the QE can't close it
// and press Approve again on the same job.
export function ApprovedScreen({ id, job_card_reference_id, install_state, stockWarning }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [downloadCOC] = useDownloadDigitalCOCMutation();

  React.useEffect(() => { dispatch(setlogo(true)); }, []);
  const isUninstall = install_state === "UN";

  const handleDownload = () => {
    downloadCOC({ job_card_id: id })
      .unwrap()
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `job_card_${id}_digital_coc.zip`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {});
  };

  return (
    <OutcomeLayout
      icon={<CheckCircleIcon sx={{ fontSize: "2.8rem", color: "#22C55E" }} />}
      title={isUninstall ? "Uninstall approved" : "Install approved"}
      actions={
        <>
          <Button variant="contained" onClick={() => navigate("/QE/pending-installs/", { replace: true })} sx={primaryButtonSx}>
            Next pending install
          </Button>
          <Button onClick={() => navigate("/QE/", { replace: true })} sx={secondaryButtonSx}>QE home</Button>
          <Button startIcon={<ArrowDownwardIcon />} onClick={handleDownload} sx={{ ...secondaryButtonSx, border: "none" }}>
            Download COC
          </Button>
        </>
      }
    >
      <Typography sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.8)", lineHeight: 1.6 }}>
        <Box component="span" sx={{ color: AMBER, wordBreak: "break-all" }}>{job_card_reference_id}</Box>
        {isUninstall
          ? " has been approved, the digital COC is attached and the device is back in your company's stock."
          : " has been approved and the digital COC is attached."}
      </Typography>
      {stockWarning && (
        <Typography sx={{ fontSize: "0.85rem", color: "#f87171", lineHeight: 1.5, mt: "10px" }}>
          The stock record wasn't updated. Ask an IMS admin to correct it.
        </Typography>
      )}
    </OutcomeLayout>
  );
}
